import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import L from 'leaflet'

import markerIcon from 'shared/images/marker-icon.svg'
import {
  defaultErrorMessage,
  defaultIconAnchor,
  defaultIconSize,
  defaultPopupAnchor,
  defaultTileLayerAttribution,
  defaultTileLayerUrl,
  defaultZoom,
} from 'shared/ui/map/types'

import 'leaflet/dist/leaflet.css'

const cleanCoordinate = (coord: string | number): number => {
  if (typeof coord === 'number') {
    return coord
  }
  const parsed = parseFloat(String(coord).split(',')[0])

  return isNaN(parsed) ? 0 : parsed
}

interface MapMarker {
  latitude: number
  longitude: number
  title: string
  icon?: string
  iconSize?: [number, number]
  iconAnchor?: [number, number]
  popupAnchor?: [number, number]
}

interface UseMapProps {
  center: {
    latitude: number
    longitude: number
  }
  zoom?: number
  markers?: MapMarker[]
  tileLayerUrl?: string
  tileLayerAttribution?: string
  zoomControl?: boolean
  errorMessage?: string
  disableZoom?: boolean
}

interface UseMapReturn {
  isMapLoading: boolean
  mapError: string | null
  mapRef: React.RefObject<HTMLDivElement>
  handleZoomIn: () => void
  handleZoomOut: () => void
  handleRetry: () => void
  mapInstance: React.MutableRefObject<L.Map | null>
}

export const useMap = ({
  center,
  zoom = defaultZoom,
  markers = [],
  tileLayerUrl = defaultTileLayerUrl,
  tileLayerAttribution = defaultTileLayerAttribution,
  zoomControl = false,
  errorMessage = defaultErrorMessage,
  disableZoom = false,
}: UseMapProps): UseMapReturn => {
  const { t } = useTranslation()
  const [isMapLoading, setIsMapLoading] = useState(true)
  const [mapError, setMapError] = useState<string | null>(null)

  const mapRef = useRef<HTMLDivElement>(null!)
  const mapInstanceRef = useRef<L.Map | null>(null)

  const createMap = () => {
    if (!mapRef.current) {
      return
    }

    const cleanLatitude = cleanCoordinate(center.latitude)
    const cleanLongitude = cleanCoordinate(center.longitude)

    try {
      const map = L.map(mapRef.current, {
        zoomControl,
        attributionControl: false,
        scrollWheelZoom: !disableZoom,
        dragging: !disableZoom,
        doubleClickZoom: !disableZoom,
        touchZoom: !disableZoom,
        boxZoom: !disableZoom,
        keyboard: !disableZoom,
      }).setView([cleanLatitude, cleanLongitude], zoom)

      mapInstanceRef.current = map

      const tileLayer = L.tileLayer(tileLayerUrl, {
        attribution: tileLayerAttribution,
      })

      tileLayer.on('tileload', () => setIsMapLoading(false))
      tileLayer.on('tileerror', () => {
        setMapError(t(errorMessage))
        setIsMapLoading(false)
      })

      tileLayer.addTo(map)

      markers.forEach((marker) => {
        const cleanLat = cleanCoordinate(marker.latitude)
        const cleanLng = cleanCoordinate(marker.longitude)

        const icon = L.icon({
          iconUrl: marker.icon || markerIcon,
          iconSize: marker.iconSize || defaultIconSize,
          iconAnchor: marker.iconAnchor || defaultIconAnchor,
          popupAnchor: marker.popupAnchor || defaultPopupAnchor,
        })

        L.marker([cleanLat, cleanLng], { icon })
          .addTo(map)
          .bindPopup(marker.title)
      })
    } catch (error) {
      setMapError(t(errorMessage))
      setIsMapLoading(false)
    }
  }

  useEffect(() => {
    createMap()

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [center.latitude, center.longitude, zoom, markers.length])

  const handleZoomIn = () => {
    mapInstanceRef.current?.zoomIn()
  }

  const handleZoomOut = () => {
    mapInstanceRef.current?.zoomOut()
  }

  const handleRetry = () => {
    setMapError(null)

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
    }

    setTimeout(createMap, 100)
  }

  return {
    isMapLoading,
    mapError,
    mapRef,
    handleZoomIn,
    handleZoomOut,
    handleRetry,
    mapInstance: mapInstanceRef,
  }
}
